// Rendu Markdown sûr du texte d'utilisateur (corps d'un retour, commentaire).
// Construit des nœuds Preact, jamais de HTML : rien de ce qu'écrit un
// utilisateur n'est interprété comme balise. Les retours à la ligne saisis
// restent (règle « user-prose » de Faast), les jetons longs se coupent.
//
// Pris en charge : **gras**, *italique* / _italique_, `code`, blocs ```,
// listes (- * 1.), [texte](https://…), liens nus, mentions (@Nom).
import type { ComponentChildren, JSX } from 'preact'
import type { Mention } from '@feedbaacks/sdk'

const SAFE_URL = /^(https?:\/\/|mailto:)/i
// Ordre des motifs en ligne : le premier qui commence le plus tôt l'emporte.
const INLINE = /(`[^`\n]+`)|(\*\*[^*\n]+\*\*)|(\[[^\]\n]+\]\([^)\s]+\))|(https?:\/\/[^\s<]+[^\s<.,;:!?)\]'"»])|((?:^|(?<=[\s(]))[*_][^*_\n]+[*_](?=[\s.,;:!?)]|$))/g

function link(href: string, label: ComponentChildren, key: number): JSX.Element | string {
  if (!SAFE_URL.test(href)) return typeof label === 'string' ? label : href
  return (
    <a key={key} href={href} target="_blank" rel="noreferrer noopener">
      {label}
    </a>
  )
}

/** Mise en forme en ligne d'un fragment de texte. */
export function inline(text: string, mentions: Mention[] = [], keyBase = 0): ComponentChildren[] {
  const out: ComponentChildren[] = []
  let last = 0
  let k = keyBase
  for (const m of text.matchAll(INLINE)) {
    const start = m.index ?? 0
    if (start > last) out.push(...withMentions(text.slice(last, start), mentions, k++))
    const [all, code, bold, md, url, em] = m
    if (code) out.push(<code key={k++}>{code.slice(1, -1)}</code>)
    else if (bold) out.push(<strong key={k++}>{inline(bold.slice(2, -2), mentions, k * 100)}</strong>)
    else if (md) {
      const close = md.indexOf('](')
      out.push(link(md.slice(close + 2, -1), md.slice(1, close), k++))
    } else if (url) out.push(link(url, url, k++))
    else if (em) out.push(<em key={k++}>{em.slice(1, -1)}</em>)
    last = start + all.length
  }
  if (last < text.length) out.push(...withMentions(text.slice(last), mentions, k))
  return out
}

/** Remplace chaque mention connue (« @Ana ») par sa pastille. */
function withMentions(text: string, mentions: Mention[], key: number): ComponentChildren[] {
  if (!mentions.length) return [text]
  const tokens = [...new Set(mentions.map((m) => m.text).filter(Boolean))].sort((a, b) => b.length - a.length)
  const re = new RegExp(`(${tokens.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'g')
  return text.split(re).map((part, i) =>
    i % 2 === 1 ? <span key={`${key}-${i}`} class="fb-mention" data-testid="feedbaacks-mention">{part}</span> : part,
  )
}

type Block = { kind: 'p'; text: string } | { kind: 'code'; text: string } | { kind: 'ul' | 'ol'; items: string[] }

const BULLET = /^\s*[-*•]\s+/
const NUMBER = /^\s*\d+[.)]\s+/

/** Découpe en blocs : code clôturé, listes, paragraphes (sauts gardés). */
export function blocks(text: string): Block[] {
  const lines = text.replace(/\r\n?/g, '\n').split('\n')
  const out: Block[] = []
  let para: string[] = []
  const flush = () => {
    if (para.length) out.push({ kind: 'p', text: para.join('\n') })
    para = []
  }
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]
    if (line.trimStart().startsWith('```')) {
      flush()
      const code: string[] = []
      for (i++; i < lines.length && !lines[i].trimStart().startsWith('```'); i++) code.push(lines[i])
      out.push({ kind: 'code', text: code.join('\n') })
      continue
    }
    const list = BULLET.test(line) ? 'ul' : NUMBER.test(line) ? 'ol' : null
    if (list) {
      flush()
      const items: string[] = []
      const re = list === 'ul' ? BULLET : NUMBER
      for (; i < lines.length && re.test(lines[i]); i++) items.push(lines[i].replace(re, ''))
      i--
      out.push({ kind: list, items })
      continue
    }
    para.push(line)
  }
  flush()
  return out
}

/** Le texte d'un utilisateur, mis en forme sans jamais interpréter de HTML. */
export function Markdown({ text, mentions = [] }: { text: string; mentions?: Mention[] }): JSX.Element {
  return (
    <>
      {blocks(text).map((b, i) => {
        if (b.kind === 'code') return <pre key={i} class="fb-md-pre"><code>{b.text}</code></pre>
        if (b.kind === 'p') return <p key={i} class="fb-md-p">{inline(b.text, mentions)}</p>
        const items = b.items.map((it, j) => <li key={j}>{inline(it, mentions)}</li>)
        return b.kind === 'ul' ? <ul key={i} class="fb-md-list">{items}</ul> : <ol key={i} class="fb-md-list">{items}</ol>
      })}
    </>
  )
}
